<template>
  <Layout>
    <section>
      <header class="designspec-header" v-stack-y:md>
        <div class="mb-lg">
          <h1 class="mt-0 mb-sm mr-auto fw-book md:mb-0">{{ 'My Projects' | t }}</h1>
          <router-link
            :to="{
              path: 'new',
              append: true,
              params: { redirectOnCreate: true },
            }"
            custom
            class="button-create-wrapper"
          >
            <SloButton class="button-create" fill="outline">
              <SloIcon name="Plus" />
              <span>{{ 'Create new Sloan DesignSpec' | t }}</span>
            </SloButton>
          </router-link>
        </div>
        <span v-if="projects.length > 0">
          <SloInput
            v-model="nameFilter"
            type="text"
            :placeholder="'Search Projects' | t"
            class="mt-lg"
          >
            <template v-slot:start>
              <SloIcon name="MagnifyingGlass" />
            </template>
          </SloInput>
        </span>
      </header>
      <main>
        <SloLoader :loading="loading" size="xl">
          <table v-if="hasResults" class="design-spec-projects--table">
            <thead>
              <tr>
                <th class="cell-project-name">{{ 'Project Name' | t }}</th>
                <th class="cell-project-location">{{ 'Location' | t }}</th>
                <th class="cell-project-created">{{ 'Date Created' | t }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody v-if="hasResults">
              <tr v-for="project of filteredProjects" :key="project.id">
                <td class="cell-project-name">
                  <SloLink
                    :to="{
                      name: 'designspec-project',
                      params: { id: project.id },
                    }"
                  >
                    {{ project.name }}
                  </SloLink>
                </td>
                <td class="cell-project-location">
                  <span
                    v-if="project.city || project.state"
                    v-html="[project.city, project.state].filter((v) => v).join(', ')"
                  />
                  <span v-else class="cell-project-location-none">
                    ({{ 'none' | t }})
                  </span>
                </td>
                <td class="cell-project-created">
                  <span v-if="project.date">{{ formatDate(project.date) }}</span>
                </td>
                <td>
                  <div class="cell-actions">
                    <SloButton fill="clear" @click="handleDelete(project.id)">
                      <SloIcon name="Trash" />
                    </SloButton>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="design-spec-projects--empty-state" v-if="!hasResults">
            <p v-if="projects.length === 0">{{ 'You have not created any projects yet!' | t }}</p>
            <p v-else>{{ 'No results found' | t }}</p>
          </div>
        </SloLoader>
      </main>
    </section>
  </Layout>
</template>

<script>
import {mapState, mapActions, mapGetters} from 'vuex';
import t from '../../main/i18n';
import Layout from '../components/Layout.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloLink from '../../../lib/components/SloLink.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';

export default {
  components: {
    Layout,
    SloIcon,
    SloButton,
    SloLink,
    SloInput,
    SloLoader
  },
  data() {
    return {
      loading: false,
      nameFilter: ''
    };
  },
  filters: {
    t
  },
  async created() {
    this.loading = true;
    await this.$store.dispatch('designspec/getProjects');
    this.loading = false;
  },
  computed: {
    ...mapGetters('auth', ['authenticated']),
    ...mapState('designspec', ['projects']),
    hasResults() {
      return this.projects.length && this.filteredProjects.length > 0;
    },
    filteredProjects() {
      if (JSON.parse(JSON.stringify(this.projects))) {
        return this.projects.filter(project => {
          if (this.nameFilter && project?.name) {
            return (
              project.name.toLowerCase().indexOf(this.nameFilter.toLowerCase()) > -1
            );
          }

          return true;
        });
      }

      return [];
    }
  },
  watch: {
    nameFilter() {
      this.loading = true;
      setTimeout(() => this.loading = false, 375);
    }
  },
  methods: {
    ...mapActions('designspec', ['deleteProject']),
    formatDate(date) {
      if (date) {
        if (date.length === 10) {
          date = date + '000';
        }
        return new Date(Number(date)).toLocaleDateString();
      }
      return '';
    },
    async handleDelete(id) {
      this.loading = true;
      await this.deleteProject({id});
      this.loading = false;
      window.scrollTo(0, 0);
      this.$snotify.clear();
      this.$snotify.success(t('Project deleted'));
    },
    displayError(e, message) {
      this.$snotify.error(t(message));
    }
  }
};
</script>

<style lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.designspec-header {
  .button-create-wrapper {
    width: 100%;

    @media screen and (min-width: $screen-sm-min) {
      width: unset;
    }
  }
  .button-create {
    width: 100%;
    margin-top: 16px;
    min-height: 48px;

    @media screen and (min-width: $screen-sm-min) {
      width: unset;
      margin-top: 0;
    }
  }
}

.design-spec-projects--table {
  border-collapse: separate;
  border: 1px solid $color-gray-400;
  border-radius: 1.2rem;
  overflow: hidden;

  thead {
    background-color: $color-gray-100;
  }

  th,
  td {
    padding: 20px;
    vertical-align: middle;

    &:first-of-type {
      padding-left: 20px !important;
    }
  }

  th {
    font-size: $font-size-base;
    font-weight: $font-weight-medium;
    text-transform: capitalize;
    color: $color-gray-900;
    border-bottom: none;
  }

  .cell-checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
  }

  .cell-project-name {
    width: 320px;
    padding: 20px;
  }

  .cell-project-created,
  .cell-project-location {
    display: none;

    @media screen and (min-width: $screen-sm-min) {
      display: table-cell;
      width: 170px;
    }
  }

  .cell-project-location-none {
    color: $gray-dark;
    font-style: italic;
  }

  .cell-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    button {
      margin: -10px;
      padding: 10px;
    }
  }
}

.design-spec-projects--empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;

  @media screen and (min-width: $screen-sm-min) {
    padding: 80px;
  }

  flex: 1 0 auto;

  > p {
    @include text('2xl');
    text-align: center;
    color: $color-gray-700;
  }
}
</style>
